import { Box, Stack } from '@mui/material'
import { Container, Typography } from '@evocolabs/orkaui'
import { ContentTitle, SectionTitle } from '../../title'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Autoplay } from 'swiper'
import Laptop40 from '@evocolabs/icons/lib/Laptop40'
import Phone40 from '@evocolabs/icons/lib/Phone40'
import TV40 from '@evocolabs/icons/lib/TV40'
import styles from './styles.module.scss'
import theme from '@/src/theme'
import { useState } from 'react'

const data = {
  title: 'Bluetooth® 5.3: Connect you to the world',
  left: {
    title: 'Multi-Device Connectivity',
    subTitle:
      'Orka Two supports multiple Bluetooth connections simultaneously. ',
    moreContent:
      'Stay connected to your iPhone, Android devices, laptops and more. Switch between your connected devices without hitting “disconnect”.',
    more: 'View More',
    less: 'View Less'
  },
  top: {
    title: 'Calls/Music/TV Streaming',
    // eslint-disable-next-line max-len
    subTitle: `Orka Two supports audio streaming with no extra accessories needed.
    Take phone calls and enjoy your favorite music all through your
    hearing aids now.`
  }
}

const SwiperList = [
  {
    img: 'https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/compatible+devices_img_phone.jpg',
    title: 'Android & iOS'
  },
  {
    img: 'https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/compatible_devices_img_laptop_241212.jpg',
    title: 'Laptop'
  },
  {
    img: 'https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/compatible+devices_img_tv.jpg',
    title: 'TV'
  }
]

type SWIPER_ELEMENT_TYPE = HTMLElement & {
  swiper: { slideToLoop: (index: number) => void }
}

const deviceList = [Phone40, Laptop40, TV40]

const UniversalCompatibility = () => {
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0)
  const [showMore, setShowMore] = useState<boolean>(false)

  const handleSwiper = (index) => {
    const swiperElement = document.querySelector('#compatibilitySwiperPC')
    if (swiperElement && 'swiper' in swiperElement) {
      ;(swiperElement as SWIPER_ELEMENT_TYPE).swiper.slideToLoop(index)
    }
  }

  return (
    <Box id="compatibility" className={styles.compatibility}>
      <Stack width={{ xs: '100%', dl: '40%' }} justifyContent="center">
        <ContentTitle
          className={`${styles['content-title']} ${styles['content-title__spacing']}`}
        >
          {data.left.title}
        </ContentTitle>
        <Stack gap={2} justifyContent="space-between">
          <Stack px={{ xs: 3, dl: 8 }} pt={{ xs: 3, dl: 2 }} gap={2}>
            <Typography variant="body1" as="div">
              {data.left.subTitle}
              <Typography
                variant="body1"
                display={showMore ? 'flex' : 'none'}
                style={{ transition: 'display 1s ease-in-out' }}
              >
                {data.left.moreContent}
              </Typography>
            </Typography>

            <Typography
              variant="LINK"
              color={theme.palette.primary[500]}
              onClick={() => {
                setShowMore((prev) => !prev)
              }}
            >
              {data.left[showMore ? 'less' : 'more']}
            </Typography>
          </Stack>
          <Box
            display="flex"
            gap="12px"
            pl={{ xs: 3, dl: 8 }}
            pb={{ xs: 3, dl: 0 }}
          >
            {deviceList.map((Icon, index) => (
              <Box
                className={styles['bluetooth-device']}
                onClick={() => {
                  handleSwiper(index)
                }}
                data-is-selected={currentDeviceIndex === index ? 'yes' : ''}
                key={index}
              >
                <Icon
                  size={48}
                  replaceStrokeColor={[
                    ['#26201F', theme.palette.grey[700]],
                    ['#918D8C', theme.palette.primary[700]]
                  ]}
                />
              </Box>
            ))}
          </Box>
        </Stack>
      </Stack>
      <Box
        sx={{
          width: { xs: '100%', dl: '60%' },
          height: '100%',
          '& .swiper-wrapper': { display: 'flex' }
        }}
      >
        <Swiper
          className="mySwiper"
          id="compatibilitySwiperPC"
          modules={[Autoplay]}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false
          }}
          loop
          slidesPerView={1}
          direction="horizontal"
          style={{
            height: '100%',
            overflow: 'hidden'
          }}
          onSlideChange={(swiper) => {
            setCurrentDeviceIndex(swiper.realIndex)
          }}
        >
          {SwiperList.map((item, index) => (
            <SwiperSlide key={item.title} style={{ flexShrink: 0 }}>
              <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                {index === 0 && (
                  <Box className={styles.brandLogo}>
                    <img
                      alt="GooglePlay"
                      // eslint-disable-next-line max-len
                      src="https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/Google+Play.svg"
                      loading="lazy"
                    />
                    <img
                      alt="AppStore"
                      // eslint-disable-next-line max-len
                      src="https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/App+Store.svg"
                      loading="lazy"
                    />
                  </Box>
                )}
                <img
                  className={`${styles.img} ${styles['content-media']}`}
                  alt="OrkaTwo"
                  src={item.img}
                  loading="lazy"
                />
                <Typography variant="h3" className={styles.swiperCta}>
                  {item.title}
                </Typography>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  )
}

const SmoothSwitching = () => {
  return (
    <Box id="smoothSwitch" className={styles.smoothSwitch}>
      <Box
        sx={{
          width: { xs: '100%', dl: '60%' },
          order: { xs: 1, dl: 0 }
        }}
      >
        <video
          playsInline
          autoPlay
          loop
          muted
          // eslint-disable-next-line max-len
          src="https://d2mhexn86y1lr4.cloudfront.net/website/buy-orka-two/V2/PC/device+switch_PC_compressed.mp4"
          className={`${styles.video} ${styles['content-media']}`}
        />
      </Box>

      <Stack
        justifyContent="center"
        width={{ xs: '100%', dl: '40%' }}
        order={{ xs: 0, dl: 1 }}
        padding={{ dl: '0 64px' }}
      >
        <ContentTitle className={`${styles['content-title']}`}>
          {data.top.title}
        </ContentTitle>
        <Typography
          variant="body1"
          marginTop={{ xs: '24px', dl: '16px' }}
          margin={{ xs: '24px', dl: '16px 0 0' }}
        >
          {data.top.subTitle}
        </Typography>
      </Stack>
    </Box>
  )
}

const BlueTooth = function () {
  return (
    <Box mt={{ xs: 0, dl: 0 }}>
      <Container
        sx={{
          paddingY: { xs: 5, dl: 10 }
        }}
      >
        <SectionTitle>{data.title}</SectionTitle>
        <UniversalCompatibility />
        <SmoothSwitching />
      </Container>
    </Box>
  )
}

export default BlueTooth
